import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Empty, FilterButton, H3, LayoutSidebar, SectionHeader, SidebarButton, SidebarContent, SidebarFooter, SidebarHeader, SidebarHeaderTitle } from 'components';
import { DEFAULT_ORG_ADMIN_JOB_TITLE } from 'config/const';
import ListContext, { FetchDataQueryType, ListContextProvider } from 'providers/ListContextProvider';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { Employees, selectEmployees } from 'store/Collections';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { Payment } from 'store/Payment';
import { ICurrentEmployee, IStore } from 'store/types';
import { EmployeeType, IApiQueryParam, IEmployee, IPayee, ISiteRolePolicy } from 'types';
import { formatInviteRecipient, formatJobTitle } from 'utils';
import { DEFAULT_SORT } from '../Users';
import UsersFilters from '../Users/UsersFilters';
import { AddedList, AddedListItem, MPContent, SidebarMobileActions } from './components';
import { SelectPayeesTable } from './components/SelectPayeesTable';
import MakePaymentHeader from './MakePaymentHeader';
import { MakePaymentProps, MakePaymentSidebarProps } from './MakePaymentTypes';

interface ISelectPayeesProps extends MakePaymentProps, MakePaymentSidebarProps {
  employees: IEmployee[];
  payees: IPayee[];
  onAddToPayeesList: (id: string) => void;
  onAddAllEmployeesToPayeesList: (query: FetchDataQueryType) => void;
  onRemovePayee: (index: number) => void;
  fetchData: (query: FetchDataQueryType) => void;
  rolePolicy: ISiteRolePolicy;
  currentEmployee: ICurrentEmployee;
}

const SelectPayees = ({
  employees,
  onExpand,
  onBack,
  onNext,
  sidebarOpen,
  payees,
  currentSiteId,
  pageHeaderContent,
  onAddToPayeesList,
  onAddAllEmployeesToPayeesList,
  onRemovePayee,
  fetchData,
  rolePolicy,
  currentEmployee,
}: ISelectPayeesProps) => {
  const [filterActive, setFilterActive] = useState<boolean>(false);
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employees.GET_EMPLOYEES_REQUEST));
  const isLoadingAddAll = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payment.GET_PAYEES_REQUEST));
  const { total } = useSelector(selectEmployees()) || { data: [], total: null };
  const currentSite = useSelector(selectCurrentEmployeeSite);

  const disableNext = !payees || payees.length === 0 || isLoading || isLoadingAddAll;

  return (
    <LayoutSidebar
      content={
        <>
          <MakePaymentHeader pageHeaderContent={pageHeaderContent} />
          <ListContextProvider fetchData={fetchData} totalSelector={() => total} defaultSort={DEFAULT_SORT} persistURL dataKey="users" dependencies={currentSiteId}>
            <ListContext.Consumer>
              {({ query }) => (
                <MPContent>
                  <SectionHeader title="Select Payees">
                    <FilterButton data-cy="toggle-payees-filters-btn" filterActive={filterActive} appliedFilters={query.q?.filter((item: IApiQueryParam) => item.key !== 'status').length} onClick={() => setFilterActive(!filterActive)} />
                    <Button
                      data-cy="add-all-btn"
                      label="Add All"
                      appearance="primary"
                      size="small"
                      onClick={() => onAddAllEmployeesToPayeesList({ ...query, meta: currentSiteId })}
                      isProcessing={isLoadingAddAll}
                      disabled={isLoading || isLoadingAddAll || employees?.length === 0}
                    />
                  </SectionHeader>

                  {employees && <UsersFilters isVisible={filterActive} data-cy="payees-filters" roleVisible={false} statusVisible={false} jobTitleVisible nested={false} site={currentSite} />}

                  <SelectPayeesTable
                    onAddToPayeesList={onAddToPayeesList}
                    rolePolicy={rolePolicy}
                    currentEmployee={currentEmployee}
                    data={
                      employees
                        ? employees.map((employee: IEmployee) => ({
                            id: employee.id,
                            username: employee.name,
                            avatarUrl: employee.user?.avatarUrl,
                            type: employee.type as EmployeeType,
                            status: employee.status,
                            actionRequired: employee.actionRequired,
                            inviteRecipient: employee.inviteRecipient ? formatInviteRecipient(employee.inviteRecipient) : '',
                            sites: employee.sites,
                            createdAt: employee.createdAt,
                            hasPaymentMethod: !!employee.user?.card,
                            isAdded: !!payees?.find((payee: IPayee) => employee.id === payee.id),
                          }))
                        : []
                    }
                    isMultiSite
                    isLoading={isLoading || isLoadingAddAll}
                    tableHeaders={[
                      {
                        label: 'Name',
                        value: 'name',
                      },
                      {
                        label: 'Job Title',
                        value: 'jobTitle',
                      },
                      {
                        label: '',
                        value: 'addToPayeesList',
                        align: 'right',
                      },
                    ]}
                  />
                </MPContent>
              )}
            </ListContext.Consumer>
          </ListContextProvider>
        </>
      }
      sidebar={
        <>
          <SidebarHeader>
            <SidebarHeaderTitle>
              <SidebarButton onClick={onExpand} isOpen={sidebarOpen} />
              <H3>Payees ({payees ? payees.length : 0})</H3>
            </SidebarHeaderTitle>

            <SidebarMobileActions columns={1}>
              <Button label="Back" data-cy="mobile-back-btn" appearance="white" size="small" onClick={onBack} />
              <Button label="Next" data-cy="mobile-next-btn" appearance="primary" size="small" disabled={disableNext} onClick={onNext} />
            </SidebarMobileActions>
          </SidebarHeader>

          <SidebarContent>
            {payees && payees.length >= 1 ? (
              <AddedList data-cy="select-payees-added-list">
                {payees.map((payee: IPayee, index: number) => {
                  const { user } = payee;
                  return (
                    <AddedListItem
                      data-cy="added-list-item"
                      data-value={payee.id}
                      key={index}
                      id={payee.id}
                      username={payee.name}
                      image={user && user.avatarUrl}
                      role={formatJobTitle(payee.type === EmployeeType.ADMIN ? DEFAULT_ORG_ADMIN_JOB_TITLE : payee.sites[0]?.job.title)}
                      hasPaymentMethod={!!user?.card}
                      isLoading={isLoadingAddAll}
                      onRemove={() => onRemovePayee(index)}
                    />
                  );
                })}
              </AddedList>
            ) : (
              <Empty
                container
                icon="Group"
                message={
                  <>
                    Payees will appear here
                    <br />
                    as you add them.
                  </>
                }
              />
            )}
          </SidebarContent>

          <SidebarFooter>
            <Button label="Back" data-cy="back-btn" appearance="white" size="large" onClick={onBack} />
            <Button label="Next" data-cy="next-btn" appearance="primary" size="large" disabled={disableNext} onClick={onNext} />
          </SidebarFooter>
        </>
      }
      sidebarOpen={sidebarOpen}
    />
  );
};

export default SelectPayees;
